import {
  LISTING_PAYMENT_STATUSES,
  type ListingPaymentStatus,
  type RecoverCurrentListingPaymentResponse,
  type RecoveredListingPayment,
} from "../types/listingPayment";
import { deriveCanonicalPaidBusinessId } from "./listingPaymentIdentifiers";

const PAYMENT_ID_PATTERN = /^pay_[a-f0-9]{40}$/;
const ENTITLEMENT_ID_PATTERN = /^ent_pay_[a-f0-9]{40}$/;
const RECOVERED_PAYMENT_BASE_KEYS = [
  "paymentId",
  "planId",
  "planName",
  "amount",
  "currency",
  "method",
  "status",
  "maskedPhone",
];
const PAYMENT_AUDIT_KEYS = [
  "canonicalPlanAmount",
  "environment",
  "isSandboxTestPayment",
];

export class ListingPaymentProtocolError extends Error {
  constructor(message = "The payment response requires review.") {
    super(message);
    this.name = "ListingPaymentProtocolError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: string[]) {
  const actual = Object.keys(value).sort();
  const required = [...expected].sort();
  return (
    actual.length === required.length &&
    actual.every((key, index) => key === required[index])
  );
}

function isPaymentStatus(value: unknown): value is ListingPaymentStatus {
  return LISTING_PAYMENT_STATUSES.includes(value as ListingPaymentStatus);
}

function isPaymentId(value: unknown): value is string {
  return typeof value === "string" && PAYMENT_ID_PATTERN.test(value);
}

function isEntitlementId(value: unknown): value is string {
  return typeof value === "string" && ENTITLEMENT_ID_PATTERN.test(value);
}

function hasValidPaymentCharge(value: Record<string, unknown>) {
  const hasAnyAuditField = PAYMENT_AUDIT_KEYS.some(
    (key) => value[key] !== undefined
  );

  if (!hasAnyAuditField) return value.amount === 5000;

  if (
    value.canonicalPlanAmount !== 5000 ||
    (value.environment !== "sandbox" && value.environment !== "production") ||
    typeof value.isSandboxTestPayment !== "boolean"
  ) {
    return false;
  }

  return value.isSandboxTestPayment
    ? value.amount === 1 && value.environment === "sandbox"
    : value.amount === 5000 ||
        (value.amount === 1 && value.environment === "production");
}

function requireRecoveredPayment(value: unknown): RecoveredListingPayment {
  if (
    !isRecord(value) ||
    !isPaymentId(value.paymentId) ||
    !isPaymentStatus(value.status) ||
    value.planId !== "basic_business_listing_annual" ||
    value.planName !== "Basic Business Listing" ||
    !hasValidPaymentCharge(value) ||
    value.currency !== "KES" ||
    value.method !== "mpesa_paybill_stk_push" ||
    typeof value.maskedPhone !== "string" ||
    !/^254[17]\*{5}\d{3}$/.test(value.maskedPhone)
  ) {
    throw new ListingPaymentProtocolError();
  }

  if (value.status === "succeeded") {
    const baseKeys = [
      ...RECOVERED_PAYMENT_BASE_KEYS,
      ...(value.canonicalPlanAmount === undefined ? [] : PAYMENT_AUDIT_KEYS),
    ];
    if (
      !hasExactKeys(value, [
        ...baseKeys,
        "entitlementId",
      ]) ||
      !isEntitlementId(value.entitlementId) ||
      value.entitlementId !== `ent_${value.paymentId}`
    ) {
      throw new ListingPaymentProtocolError();
    }
  } else if (
    !hasExactKeys(value, [
      ...RECOVERED_PAYMENT_BASE_KEYS,
      ...(value.canonicalPlanAmount === undefined ? [] : PAYMENT_AUDIT_KEYS),
    ])
  ) {
    throw new ListingPaymentProtocolError();
  }

  return value as RecoveredListingPayment;
}

export function parseRecoverCurrentListingPaymentResponse(
  value: unknown
): RecoverCurrentListingPaymentResponse {
  if (!isRecord(value) || typeof value.status !== "string") {
    throw new ListingPaymentProtocolError();
  }

  if (value.status === "none") {
    if (!hasExactKeys(value, ["status"])) {
      throw new ListingPaymentProtocolError();
    }
    return { status: "none" };
  }

  if (value.status === "verification_required") {
    if (value.payment === undefined) {
      if (!hasExactKeys(value, ["status"])) {
        throw new ListingPaymentProtocolError();
      }
      return { status: "verification_required" };
    }
    if (!hasExactKeys(value, ["status", "payment"])) {
      throw new ListingPaymentProtocolError();
    }
    const payment = requireRecoveredPayment(value.payment);
    if (payment.status !== "verification_required") {
      throw new ListingPaymentProtocolError();
    }
    return { status: "verification_required", payment };
  }

  if (value.status === "consumed") {
    if (
      !hasExactKeys(value, [
        "status",
        "payment",
        "entitlementId",
        "businessId",
      ]) ||
      !isEntitlementId(value.entitlementId) ||
      value.businessId !== deriveCanonicalPaidBusinessId(value.entitlementId)
    ) {
      throw new ListingPaymentProtocolError();
    }
    const payment = requireRecoveredPayment(value.payment);
    if (
      payment.status !== "succeeded" ||
      payment.entitlementId !== value.entitlementId
    ) {
      throw new ListingPaymentProtocolError();
    }
    return {
      status: "consumed",
      payment,
      entitlementId: value.entitlementId,
      businessId: value.businessId,
    };
  }

  if (!isPaymentStatus(value.status) || value.status === "verification_required") {
    throw new ListingPaymentProtocolError();
  }
  if (!hasExactKeys(value, ["status", "payment"])) {
    throw new ListingPaymentProtocolError();
  }
  const payment = requireRecoveredPayment(value.payment);
  if (payment.status !== value.status) {
    throw new ListingPaymentProtocolError();
  }
  return { status: value.status, payment };
}

export function parseCreatePaidListingDraftResponse(
  entitlementId: string,
  value: unknown
): { businessId: string; status: "consumed" } {
  if (
    !isEntitlementId(entitlementId) ||
    !isRecord(value) ||
    !hasExactKeys(value, ["businessId", "status"]) ||
    value.status !== "consumed" ||
    value.businessId !== deriveCanonicalPaidBusinessId(entitlementId)
  ) {
    throw new ListingPaymentProtocolError(
      "The paid business draft response requires review."
    );
  }

  return { businessId: value.businessId, status: "consumed" };
}
